import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Check } from 'lucide-react';
import { motion } from 'framer-motion';

export const Pricing = () => {
  const { t } = useLanguage();

  const plans = [
    {
      name: t('pricing_starter'),
      description: t('pricing_starter_desc'),
      price: t('pricing_starter_price'),
      vehicleLimit: '1-10',
      features: [
        t('pricing_feature_reservations'),
        t('pricing_feature_vehicles'),
        t('pricing_feature_support_email'),
      ],
      cta: t('pricing_cta'),
      popular: false,
    },
    {
      name: t('pricing_pro'),
      description: t('pricing_pro_desc'),
      price: t('pricing_pro_price'),
      vehicleLimit: '11-50',
      features: [
        t('pricing_feature_reservations'),
        t('pricing_feature_vehicles'),
        t('pricing_feature_clients'),
        t('pricing_feature_dashboard'),
        t('pricing_feature_support_email'),
        t('pricing_feature_support_phone'),
        t('pricing_feature_logitrak'),
      ],
      cta: t('pricing_cta'),
      popular: true,
    },
    {
      name: t('pricing_enterprise'),
      description: t('pricing_enterprise_desc'),
      price: t('pricing_enterprise_price'),
      vehicleLimit: '50+',
      features: [
        t('pricing_feature_reservations'),
        t('pricing_feature_vehicles'),
        t('pricing_feature_clients'),
        t('pricing_feature_dashboard'),
        t('pricing_feature_support_email'),
        t('pricing_feature_support_phone'),
        t('pricing_feature_logitrak'),
        t('pricing_feature_api'),
        t('pricing_feature_dedicated'),
      ],
      cta: t('pricing_contact'),
      popular: false,
      isEnterprise: true,
    },
  ];

  return (
    <section id="pricing" className="py-16 md:py-20 bg-slate-50" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight" data-testid="pricing-title">
            {t('pricing_title')}
          </h2>
          <p className="text-base text-slate-600 max-w-2xl mx-auto">
            {t('pricing_subtitle')}
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className={`relative bg-white rounded-2xl p-6 border ${
                plan.popular 
                  ? 'border-blue-500 shadow-lg shadow-blue-500/10' 
                  : 'border-slate-200'
              }`}
              data-testid={`pricing-card-${index}`}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {t('pricing_popular')}
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-1">{plan.name}</h3>
                <p className="text-sm text-slate-500 mb-4">{plan.description}</p>
                
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-2xl font-bold text-slate-900">
                    {plan.price}
                  </span>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  {plan.vehicleLimit} {t('pricing_vehicles')}
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-600">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Button 
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('prefill-contact', { detail: { plan: plan.name } }));
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`w-full ${
                  plan.popular 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
                data-testid={`pricing-cta-${index}`}
              >
                {plan.cta}
              </Button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
